import { cn } from '@/utils/tailwind-util';
import { observer } from 'mobx-react';
import { useEffect, useRef } from 'react';

import { useContextModal } from '@/components/Modal';

import { buildingInfoMeta } from '../constants';
import { useVenueRemarkStore } from '../store';
import { CancelDialog } from './CancelDialog';
import RemarkSubInfo from './RemarkSubInfo';

interface VenueRemarkInfoViewProps {
  onClose: () => void;
  isUpdating: boolean;
  handleSave: any;
}

type IndexRefsType = {
  [key: string]: HTMLDivElement | null;
};

export default observer(function VenueRemarkInfoView(
  props: VenueRemarkInfoViewProps
) {
  const { isUpdating, handleSave } = props;
  const { onClose } = props;
  const modal = useContextModal();
  const store = useVenueRemarkStore();
  const indexRefs = useRef<IndexRefsType>({});

  const handleIndexClick = (key: string) => () => {
    store.setSelectedIndex(key);
    indexRefs.current[key]?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClose = () => {
    if (store.editMode) {
      modal.open([
        <CancelDialog
          onCancel={modal.close}
          onConfirm={() => {
            store.setEditMode(false);
            modal.close();
            onClose();
          }}
        />,
        { size: 'auto', isOverlayClickable: false },
      ]);
    } else {
      onClose();
    }
  };

  const handleMainButtonClick = () => {
    if (!store.editMode) {
      store.setEditMode(true);
    } else {
      store.setEditMode(false);
      handleSave(store.venueRemark as unknown as void);
    }
  };

  useEffect(() => {
    handleIndexClick(store.selectedIndex)();
  }, []);

  const BUTTON_STYLE = {
    selected: 'rounded-full px-5 py-3 text-base font-semibold bg-[#0e2130] text-white',
    unselected: 'rounded-full px-5 py-3 text-base font-semibold bg-[#f5f5f5] text-[#1a3d57]',
  };

  return (
    <>
      <div className="flex max-h-[746px] w-[800px] flex-col">
        <div className="text-xl font-medium text-[#1c1c1e]">
          공간 부가정보
          <div className="mt-[10px] flex items-center gap-[10px]">
            {Object.values(buildingInfoMeta).map((info) => (
              <button
                className={
                  cn(BUTTON_STYLE[
                    store.selectedIndex === info.key ? 'selected' : 'unselected'
                  ])
                }
                key={info.key}
                onClick={handleIndexClick(info.key)}
              >
                {info.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 flex h-[564px] flex-col overflow-y-auto">
          <div className="flex flex-col gap-10">
            {Object.values(buildingInfoMeta).map((info) => (
              <div
                key={info.label}
                ref={(el) => (indexRefs.current[info.key] = el)}
                className="flex flex-col text-base font-medium first-of-type:mt-4"
              >
                <div className="text-lg font-semibold">{info.label}</div>
                <RemarkSubInfo subInfo={info.subInfo} />
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            className="mt-4 h-12 w-20 rounded bg-[#F6F8FF] text-base font-medium text-[#475AC1] hover:opacity-70"
            onClick={handleClose}
          >
            닫기
          </button>
          <button
            className="ml-4 mt-4 h-12 w-20 rounded bg-[#475AC1] text-base font-medium text-white hover:opacity-70"
            disabled={isUpdating}
            onClick={handleMainButtonClick}
          >
            {store.editMode ? '저장' : '수정'}
          </button>
        </div>
      </div>
    </>
  );
});
